/** Key-value persistence port (P3 adapter: Capacitor Preferences). */
export interface KeyValueStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export const KEYS = {
  deviceId: 'rt.mobile.deviceId',
  lastCheckIn: 'rt.mobile.lastCheckIn',
  pendingBundle: 'rt.mobile.pendingBundle',
  downloadedBundle: 'rt.mobile.downloadedBundle',
  lastApply: 'rt.mobile.lastApply',
  recommendSnoozeUntil: 'rt.mobile.recommendSnoozeUntil',
  pushDeclinedAt: 'rt.mobile.push.declinedAt',
  bioEnabled: 'rt.mobile.bio.enabled',
  bioDeclined: 'rt.mobile.bio.declined',
  bioLaunches: 'rt.mobile.bio.launches',
} as const;

// Storage must never take the shell down: every access degrades quietly.
export async function safeGet(store: KeyValueStore, key: string): Promise<string | null> {
  try { return await store.get(key); } catch { return null; }
}
export async function safeSet(store: KeyValueStore, key: string, value: string): Promise<void> {
  try { await store.set(key, value); } catch { /* unavailable/full — keep going */ }
}
export async function safeRemove(store: KeyValueStore, key: string): Promise<void> {
  try { await store.remove(key); } catch { /* keep going */ }
}

export async function readJson<T>(store: KeyValueStore, key: string, guard: (v: unknown) => v is T): Promise<T | null> {
  const raw = await safeGet(store, key);
  if (!raw) return null;
  try {
    const v: unknown = JSON.parse(raw);
    return guard(v) ? v : null;
  } catch {
    return null;
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Stable anonymous id for this install (rollout bucket + stats). Never a user id. */
export async function getOrCreateDeviceId(store: KeyValueStore, uuid: () => string): Promise<string> {
  const existing = await safeGet(store, KEYS.deviceId);
  if (existing && UUID_RE.test(existing)) return existing;
  const id = uuid();
  await safeSet(store, KEYS.deviceId, id);
  return id;
}
